import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@/api/client'
import { campaignNameErrorMessage } from '@/api/campaign-name-errors'
import { useParams, useNavigate } from 'react-router-dom'
import {
  useCampaign,
  useAdGroups,
  useUpdateCampaignState,
  useCampaignAliases,
  useRenameCampaign,
  useRevertCampaignName,
} from '@/api/campaigns'
import { StateBadge, AdTypeBadge } from '@/components/StatusBadge'
import { ArrowLeft, Pause, Play, Package, Edit3, RotateCcw, History, AlertTriangle, CheckCircle2 } from 'lucide-react'

function formatNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return String(n)
}
function formatEur(n: number): string {
  return `${n.toFixed(2)} EUR`
}

function KpiCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-[var(--border)] bg-[var(--card)] p-4">
      <p className="text-xs text-[var(--muted-foreground)]">{label}</p>
      <p className="mt-1 text-lg font-semibold tabular-nums">{value}</p>
    </div>
  )
}

function Skeleton({ h = 'h-4', w = 'w-full' }: { h?: string; w?: string }) {
  return <div className={`animate-pulse rounded bg-[var(--muted)] ${h} ${w}`} />
}

export default function CampaignDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const campaignId = id ? parseInt(id, 10) : null

  const { data, isLoading, error } = useCampaign(campaignId)
  const { data: adGroupsData } = useAdGroups(campaignId)
  const updateState = useUpdateCampaignState()

  const [isEditingName, setIsEditingName] = useState(false)
  const [nameInput, setNameInput] = useState('')
  const [pilotSuccessMsg, setPilotSuccessMsg] = useState<string | null>(null)
  const [pilotErrorMsg, setPilotErrorMsg] = useState<string | null>(null)

  const { data: aliasesData } = useCampaignAliases(campaignId)
  const renameMutation = useRenameCampaign()
  const revertMutation = useRevertCampaignName()

  const campaign = data?.campaign
  const adGroups = adGroupsData?.adGroups ?? []
  const aliases = aliasesData?.aliases ?? []
  const newestAlias = aliases[0]
  const canRevert = newestAlias !== undefined
    && newestAlias.newName === campaign?.name
    && newestAlias.oldName !== campaign?.name

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton h="h-6" w="w-1/3" />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="rounded-lg border border-[var(--border)] bg-[var(--card)] p-4 space-y-2">
              <Skeleton h="h-3" w="w-16" />
              <Skeleton h="h-6" w="w-24" />
            </div>
          ))}
        </div>
        <Skeleton h="h-40" />
      </div>
    )
  }
  if (error) return <div className="py-12 text-center text-sm text-red-500">Fehler: {(error as Error).message}</div>
  if (!campaign) return <div className="py-12 text-center text-sm text-[var(--muted-foreground)]">Campaign nicht gefunden</div>

  const m7 = campaign.metrics7d
  const m30 = campaign.metrics30d ?? m7
  const acos7 = m7.sales > 0 ? ((m7.spend / m7.sales) * 100) : 0
  const ctr7 = m7.impressions > 0 ? ((m7.clicks / m7.impressions) * 100) : 0
  const acos30 = m30.sales > 0 ? ((m30.spend / m30.sales) * 100) : 0
  const roas30 = m30.spend > 0 ? (m30.sales / m30.spend) : 0

  function handleToggleState() {
    if (!campaign) return
    const newState = campaign.state === 'enabled' ? 'paused' : 'enabled'
    updateState.mutate({ id: campaign.id, state: newState })
  }

  function handleRename() {
    if (!campaign || !nameInput.trim()) return
    setPilotSuccessMsg(null)
    setPilotErrorMsg(null)
    renameMutation.mutate(
      { id: campaign.id, name: nameInput.trim(), expectedName: campaign.name },
      {
        onSuccess: (res) => {
          setIsEditingName(false)
          setPilotSuccessMsg(`Kampagne erfolgreich umbenannt in "${res.campaign.name}".`)
        },
        onError: (err: unknown) => {
          setPilotErrorMsg(campaignNameErrorMessage(err, 'rename'))
          if (err instanceof ApiError && err.code === 'CAMPAIGN_NAME_CHANGED') {
            setIsEditingName(false)
            void queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id] })
            void queryClient.invalidateQueries({ queryKey: ['campaignAliases', campaign.id] })
          }
        },
      },
    )
  }

  function handleRevert() {
    if (!campaign) return
    setPilotSuccessMsg(null)
    setPilotErrorMsg(null)
    revertMutation.mutate(
      { id: campaign.id },
      {
        onSuccess: (res) => {
          setPilotSuccessMsg(`Kampagnenname erfolgreich auf Original "${res.campaign.name}" zurückgesetzt.`)
        },
        onError: (err: unknown) => {
          setPilotErrorMsg(campaignNameErrorMessage(err, 'revert'))
        },
      },
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <button
            onClick={() => navigate('/campaigns')}
            className="mb-2 flex items-center gap-1 text-sm text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
          >
            <ArrowLeft className="h-4 w-4" /> Zurueck
          </button>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold">{campaign.name}</h1>
            <AdTypeBadge adType={campaign.adType} />
            <StateBadge state={campaign.state} />
          </div>
          <p className="mt-1 text-sm text-[var(--muted-foreground)]">
            Budget: {campaign.budget ? `${parseFloat(campaign.budget).toFixed(2)} EUR/${campaign.budgetType}` : '—'}
            {' | '}Targeting: {campaign.targetingType ?? '—'}
            {' | '}Start: {campaign.startDate ?? '—'}
          </p>
        </div>
        {campaign.state !== 'archived' && (
          <button
            onClick={handleToggleState}
            disabled={updateState.isPending}
            className="flex items-center gap-2 rounded-md border border-[var(--border)] px-3 py-2 text-sm hover:bg-[var(--muted)]"
          >
            {campaign.state === 'enabled' ? (
              <><Pause className="h-4 w-4 text-amber-600" /> Pausieren</>
            ) : (
              <><Play className="h-4 w-4 text-emerald-600" /> Aktivieren</>
            )}
          </button>
        )}
      </div>

      {/* Pilot S1 Section */}
      <div className="rounded-lg border border-indigo-500/30 bg-indigo-50/10 p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="rounded bg-indigo-600 px-2 py-0.5 text-xs font-semibold text-white">
              Pilot S1
            </span>
            <h2 className="text-sm font-semibold text-[var(--foreground)]">
              Kampagnenname & Standardisierung
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {canRevert && (
              <button
                onClick={handleRevert}
                disabled={revertMutation.isPending}
                className="flex items-center gap-1.5 rounded-md border border-amber-600/30 bg-amber-500/10 px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-500/20 disabled:opacity-50 dark:text-amber-400"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                {revertMutation.isPending ? 'Wird zurückgesetzt...' : 'Auf Originalnamen zurücksetzen'}
              </button>
            )}
            {!isEditingName && (
              <button
                onClick={() => {
                  setIsEditingName(true)
                  setNameInput(campaign.name)
                  setPilotSuccessMsg(null)
                  setPilotErrorMsg(null)
                }}
                className="flex items-center gap-1.5 rounded-md border border-indigo-600/30 bg-indigo-500/10 px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-500/20 dark:text-indigo-400"
              >
                <Edit3 className="h-3.5 w-3.5" />
                Nach Standard umbenennen
              </button>
            )}
          </div>
        </div>

        {pilotSuccessMsg && (
          <div className="flex items-center gap-2 rounded-md border border-emerald-500/30 bg-emerald-50/20 p-2.5 text-xs text-emerald-700 dark:text-emerald-400">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            <span>{pilotSuccessMsg}</span>
          </div>
        )}

        {pilotErrorMsg && (
          <div className="flex items-center gap-2 rounded-md border border-red-500/30 bg-red-50/20 p-2.5 text-xs text-red-600 dark:text-red-400">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>{pilotErrorMsg}</span>
          </div>
        )}

        {isEditingName && (
          <div className="rounded-md border border-[var(--border)] bg-[var(--card)] p-3 space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-[var(--foreground)]">
                Neuer Name (5-Slot-Standard: z.B. 01_SP_DE_CAMP_TEST oder 05_SCA_SP_KW_Exact_Scale):
              </label>
              <button
                type="button"
                onClick={() => setIsEditingName(false)}
                className="text-xs text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
              >
                Abbrechen
              </button>
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                placeholder="05_SCA_SP_KW_Exact_Scale"
                className="flex-1 rounded-md border border-[var(--border)] bg-[var(--background)] px-3 py-1.5 font-mono text-xs text-[var(--foreground)] focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
              <button
                onClick={handleRename}
                disabled={renameMutation.isPending || !nameInput.trim()}
                className="rounded-md bg-indigo-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {renameMutation.isPending ? 'Speichern...' : 'Umbenennen ausführen'}
              </button>
            </div>
          </div>
        )}

        {aliases.length > 0 && (
          <div className="space-y-1.5 pt-1">
            <h3 className="flex items-center gap-1 text-xs font-medium text-[var(--muted-foreground)]">
              <History className="h-3.5 w-3.5" /> Namenshistorie (Aliases)
            </h3>
            <div className="overflow-x-auto rounded border border-[var(--border)] bg-[var(--card)]">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-[var(--border)] bg-[var(--muted)]/50 text-[var(--muted-foreground)]">
                    <th className="px-3 py-1.5 text-left font-medium">Ursprünglicher Name</th>
                    <th className="px-3 py-1.5 text-left font-medium">Neuer Name</th>
                    <th className="px-3 py-1.5 text-right font-medium">Geändert am</th>
                  </tr>
                </thead>
                <tbody>
                  {aliases.map((a) => (
                    <tr key={a.id} className="border-b border-[var(--border)]/50 last:border-0">
                      <td className="px-3 py-1.5 font-mono text-[var(--muted-foreground)]">{a.oldName}</td>
                      <td className="px-3 py-1.5 font-mono font-medium text-[var(--foreground)]">{a.newName}</td>
                      <td className="px-3 py-1.5 text-right text-[var(--muted-foreground)] tabular-nums">
                        {new Date(a.renamedAt).toLocaleString('de-DE')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {campaign.parentInfo && (
        <div className="flex items-center gap-3 rounded-md border border-[var(--border)] bg-[var(--muted)]/30 px-4 py-3 text-sm">
          <Package className="h-4 w-4 text-indigo-600" />
          <span className="text-[var(--muted-foreground)]">Portfolio:</span>
          <button
            onClick={() => navigate('/portfolios')}
            className="font-medium hover:underline"
          >
            {campaign.parentInfo.name}
          </button>
          <span className="font-mono text-xs text-[var(--muted-foreground)]">{campaign.parentInfo.amazonParentAsin}</span>
          {campaign.parentInfo.ppcCode && (
            <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-zinc-700">
              {campaign.parentInfo.ppcCode}
            </span>
          )}
          {campaign.role && (
            <span className="ml-auto text-xs text-[var(--muted-foreground)]">Rolle: <strong className="capitalize">{campaign.role}</strong></span>
          )}
        </div>
      )}

      <div>
        <h2 className="mb-3 text-sm font-medium text-[var(--muted-foreground)]">Letzte 7 Tage</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <KpiCard label="Impressions" value={formatNum(m7.impressions)} />
          <KpiCard label="Clicks" value={formatNum(m7.clicks)} />
          <KpiCard label="CTR" value={`${ctr7.toFixed(2)}%`} />
          <KpiCard label="Spend" value={formatEur(m7.spend)} />
          <KpiCard label="Sales" value={formatEur(m7.sales)} />
          <KpiCard label="ACoS" value={m7.sales > 0 ? `${acos7.toFixed(1)}%` : '—'} />
        </div>
      </div>

      <div>
        <h2 className="mb-3 text-sm font-medium text-[var(--muted-foreground)]">Letzte 30 Tage</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <KpiCard label="Impressions" value={formatNum(m30.impressions)} />
          <KpiCard label="Clicks" value={formatNum(m30.clicks)} />
          <KpiCard label="Orders" value={formatNum(m30.orders)} />
          <KpiCard label="Spend" value={formatEur(m30.spend)} />
          <KpiCard label="Sales" value={formatEur(m30.sales)} />
          <KpiCard label="ACoS / ROAS" value={m30.sales > 0 ? `${acos30.toFixed(1)}% · ${roas30.toFixed(2)}` : '—'} />
        </div>
      </div>

      <div>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-medium text-[var(--muted-foreground)]">
            Ad Groups ({adGroups.length})
          </h2>
        </div>

        {adGroups.length === 0 ? (
          <p className="py-8 text-center text-sm text-[var(--muted-foreground)]">Keine Ad Groups</p>
        ) : (
          <div className="overflow-x-auto rounded-md border border-[var(--border)]">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--border)] bg-[var(--muted)]">
                  <th className="px-3 py-2 text-left font-medium">Name</th>
                  <th className="px-3 py-2 text-left font-medium">Status</th>
                  <th className="px-3 py-2 text-right font-medium">Default Bid</th>
                  <th className="px-3 py-2 text-right font-medium">Keywords</th>
                </tr>
              </thead>
              <tbody>
                {adGroups.map((ag) => (
                  <tr
                    key={ag.id}
                    className="border-b border-[var(--border)] hover:bg-[var(--muted)] cursor-pointer"
                    onClick={() => navigate(`/keywords?adGroupId=${ag.id}`)}
                  >
                    <td className="px-3 py-2 font-medium">{ag.name}</td>
                    <td className="px-3 py-2"><StateBadge state={ag.state} /></td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {ag.defaultBid ? `${parseFloat(ag.defaultBid).toFixed(2)} EUR` : '—'}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{ag.keywordCount ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
